// Sugalarm — cycle 315: the "Alarm Reliability" checklist screen.
//
// Android-mobile-shell ONLY (the standard phone APK): walks a caregiver
// through granting the user-granted permissions the urgent-alarm path
// depends on — notifications, DND override, full-screen alarms (Android
// 14+), and battery exemption — with one Fix button per unmet item that
// deep-links straight to the matching system screen via the native
// AlarmReliability plugin. Layout per the approved 390x844 mockup: intro
// line, summary banner (warn while anything is unmet, ok when all set),
// four checklist cards, a dashed Samsung "Sleeping apps" pointer card, and
// a footer clarifying the voice/SMS escalation rail is unaffected.
//
// Statuses re-read on app resume (useAlarmReliability's foreground
// listeners), so granting a permission in system settings and returning
// flips the row to Ready with no app restart. Tokens only; translucency
// via color-mix on fills. The header/back control comes from the mobile
// Settings drill-down chrome this pane renders inside.

import {
  openAppNotificationSettings,
  openDndAccessSettings,
  openFullScreenIntentSettings,
  requestBatteryExemption,
} from "../../lib/alarmReliability";
import { launchAlarmTour } from "../../lib/alarmTour";
import type {
  ReliabilityItemState,
  UseAlarmReliability,
} from "../../hooks/useAlarmReliability";
import styles from "./AlarmReliabilitySection.module.css";

const ITEM_COPY: Record<
  ReliabilityItemState["key"],
  { title: string; desc: string; fix: () => Promise<void> }
> = {
  notifications: {
    title: "Notifications enabled",
    desc: "Sugar Alarm can post alerts on this device.",
    fix: openAppNotificationSettings,
  },
  dnd: {
    title: "Override Do Not Disturb",
    desc: "Lets urgent alarms sound while DND is on.",
    fix: openDndAccessSettings,
  },
  fullScreen: {
    title: "Full-screen alarms",
    desc: "Shows the alarm over your lock screen (Android 14+).",
    fix: openFullScreenIntentSettings,
  },
  battery: {
    title: "Battery unrestricted",
    desc: "Android will not put Sugar Alarm to sleep.",
    fix: requestBatteryExemption,
  },
};

export default function AlarmReliabilitySection({
  reliability,
}: {
  reliability: UseAlarmReliability;
}) {
  const { status, items, unmetCount, refresh } = reliability;
  const visible = items.filter((it) => it.applicable);
  const loading = status == null;
  const allMet = unmetCount === 0;

  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        Grant these so urgent glucose alarms always wake this phone, even in
        Do Not Disturb, silent mode, or deep sleep.
      </div>

      {!loading ? (
        <div
          className={`${styles.summary} ${allMet ? styles.summaryOk : styles.summaryWarn}`}
          role="status"
        >
          <span
            className={`${styles.dot} ${allMet ? styles.dotOk : styles.dotWarn}`}
            aria-hidden
          />
          {allMet
            ? "All protections are set up"
            : `${unmetCount} of ${visible.length} protection${
                visible.length === 1 ? "" : "s"
              } still need${unmetCount === 1 ? "s" : ""} setup`}
        </div>
      ) : null}

      <div className={styles.list}>
        {visible.map((it) => {
          const copy = ITEM_COPY[it.key];
          return (
            <div key={it.key} className={styles.card}>
              <span
                className={`${styles.status} ${
                  it.granted ? styles.statusOk : styles.statusWarn
                }`}
                aria-hidden
              >
                {it.granted ? "✓" : "!"}
              </span>
              <span className={styles.body}>
                <span className={styles.title}>{copy.title}</span>
                <span className={styles.desc}>{copy.desc}</span>
              </span>
              {it.granted ? (
                <span className={styles.done}>Ready</span>
              ) : (
                <button
                  type="button"
                  className={styles.fix}
                  onClick={() => {
                    // Deep-link to this app's own system settings page; the
                    // resume listener re-reads the grant on return.
                    void copy.fix().then(() => refresh());
                  }}
                >
                  Fix
                </button>
              )}
            </div>
          );
        })}

        {status?.isSamsung ? (
          <div className={styles.info}>
            <span className={styles.infoIcon} aria-hidden>
              {"ⓘ"}
            </span>
            <span className={styles.infoText}>
              <b>Samsung phones:</b> also open Settings &gt; Battery &gt;
              Background usage limits and make sure Sugar Alarm is not in the
              Sleeping apps list.
            </span>
          </div>
        ) : null}
      </div>

      {/* Cycle 316: relaunch the first-run onboarding tour on demand (the
          overlay mounts at the App level and walks the same permissions
          with the system prompts / deep-links, one screen at a time). */}
      <button
        type="button"
        className={styles.rerun}
        onClick={() => launchAlarmTour()}
      >
        Run setup again
      </button>

      <div className={styles.footer}>
        Urgent glucose alarms use these protections. Voice calls and texts to
        your emergency contacts are not affected.
      </div>
    </div>
  );
}
